import { createContext, useContext, type ReactNode } from 'react';
import { useIdentityState, type Identity } from '@core/auth/identity';
import { useMuqriRole } from '@core/firebase/roles';
import { useAdminRole } from '@core/firebase/admin';

type AuthValue = {
  identity: Identity | null;
  isMuqri: boolean;
  isAdmin: boolean;
  /**
   * True while a role is still being read. Guards must WAIT on this rather
   * than redirect: the role listeners resolve a tick after auth does, and
   * treating that tick as "not an admin" throws people out of their own
   * portal on every refresh.
   */
  loading: boolean;
  /** True while Firebase is still reading the stored session — not 'signed out'. */
  restoring: boolean;
  /**
   * True while the name is still being LOOKED UP rather than known to be
   * absent. Reading an empty name as "no name" sends a returning reader to
   * the profile step they already completed.
   */
  resolvingName: boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth used outside AuthProvider');
  return ctx;
}

/**
 * Identity and roles come from the same hooks the mobile app uses, so who
 * someone is cannot be decided differently on the web.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { identity, restoring, resolvingName } = useIdentityState();
  const { isMuqri, loading: muqriLoading } = useMuqriRole(identity?.userId ?? null);
  const { isAdmin, loading: adminLoading } = useAdminRole(identity?.userId ?? null);
  return (
    <AuthContext.Provider
      value={{
        identity, isMuqri, isAdmin, loading: muqriLoading || adminLoading,
        restoring, resolvingName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
